import React, { useState, useRef, useEffect, useCallback } from "react";
import { PMDialog } from "../../utils/PMDialog";
import { PMField } from "../../utils/PMField";
import { CameraIcon, CheckCircleIcon, XMarkIcon, FolderIcon } from "@heroicons/react/24/outline";
import { UploaderV4 } from "./Uploaderv4";
import { TWclassNames } from "../../utils/Div";
import PdfPlaceholder from "../../../utils/pdf-placeholder.png";
import { ProjectFolderSelector } from "../../utils/moduleSelectors/projectFolderSelector";

export const DocumentUploadSectionV5 = (props) => {
  const {
    multiple,
    module,
    moduleParam,
    parentCallback,
    onDocumentUploaded, // New callback for updating count
    required,
    disabled,
    iconOnly,
    buttonStyle = "default", // "default" or "compact"
    selectedFolder, // New prop for pre-selected folder
    onFolderChange, // New callback for folder changes
  } = props;
  const [open, setOpen] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedCount, setUploadedCount] = useState(0);
  const [startUpload, setStartUpload] = useState(null);
  const fileInputRef = useRef(null);
  const uploadFunctionRef = useRef(null);
  const [resetUploader, setResetUploader] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(selectedFolder || null);

  const allowedFileTypes =
    props.allowedFileTypes ||
    "image/png, image/jpeg, image/jpg, image/gif, image/svg, image/svg+xml, image/webp, application/pdf";

  // Sync folder state with prop changes
  useEffect(() => {
    setCurrentFolder(selectedFolder || null);
  }, [selectedFolder]);

  const handleFolderChange = (event) => {
    const folderId = event.target.value;
    const folder = folderId ? { id: folderId } : null;
    setCurrentFolder(folder);
    if (onFolderChange) {
      onFolderChange(folder);
    }
  };

  const handleFileSelection = (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (!selectedFiles.length) return;

    setFilesToUpload((prev) => [
      ...prev,
      ...selectedFiles.map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
        title: "",
        module,
        moduleParam,
        folder_id: currentFolder?.id || null, // Add folder information
      })),
    ]);
    setOpen(true);
  };

  const handleTitleChange = (index, title) => {
    setFilesToUpload((prev) => {
      const updated = [...prev];
      updated[index].title = title;
      return updated;
    });
  };

  const handleDeleteFile = (indexToDelete) => {
    setFilesToUpload((prev) => {
      const updated = prev.filter((_, index) => index !== indexToDelete);
      // If no files left, close the dialog
      if (updated.length === 0) {
        setOpen(false);
      }
      return updated;
    });
  };

  const handleUploadComplete = (uploadedFiles) => {
    setFilesToUpload([]);
    setIsUploading(false);
    if (parentCallback) {
      parentCallback(uploadedFiles);
    }
    // Call the new callback to update document count
    if (onDocumentUploaded) {
      onDocumentUploaded(uploadedFiles.length);
    }
    handleClose();
  };

  const handleProgressChange = (progress) => {
    setUploadProgress((prev) => {
      const newProgress = { ...prev, ...progress };
      const totalProgress = Object.values(newProgress).reduce(
        (acc, val) => acc + val,
        0
      );
      return newProgress;
    });
  };

  const handleAddMoreFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileUploadComplete = () => {
    setUploadedCount((prev) => prev + 1);
  };

  const handleClose = () => {
    setOpen(false);
    setFilesToUpload([]);
    setIsUploading(false);
    setUploadedCount(0);
    setUploadProgress({});
    uploadFunctionRef.current = null;
    setStartUpload(null);

    if (resetUploader) {
      resetUploader();
    }
  };

  // Compact button style for checklist items
  const renderCompactButton = () => (
    <button
      type="button"
      onClick={() => fileInputRef.current?.click()}
      disabled={disabled}
      className="p-1.5 text-gray-400 hover:text-purple-600 transition-colors duration-200 rounded hover:bg-purple-50 border border-blue-200 bg-blue-50"
      title="Upload Pictures"
    >
      <CameraIcon className="h-4 w-4" />
    </button>
  );

  // Default button style (original)
  const renderDefaultButton = () => (
    <div
      className={TWclassNames(
        required ? "border-2 border-red-400" : "",
        "text-center bg-blue-400 hover:bg-blue-600 border-gray-300 px-5 py-5"
      )}
    >
      <label
        htmlFor={"file-upload"}
        className={TWclassNames(
          disabled ? "cursor-not-allowed" : "bg-white",
          "relative cursor-pointer rounded-md font-medium text-white focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-400 focus-within:ring-offset-2"
        )}
      >
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="w-full h-full flex flex-row items-center justify-center"
        >
          <CameraIcon className="h-5 w-6 mr-2 text-white" />
          {!iconOnly && (
            <div className="flex-row">
              <span>Last opp dokument/ bilde</span>
            </div>
          )}
        </button>
      </label>
    </div>
  );

  return (
    <>
      {/* Hidden File Input */}
      <input
        accept={allowedFileTypes}
        type="file"
        multiple={multiple}
        ref={fileInputRef}
        className="sr-only"
        onChange={handleFileSelection}
      />

      {/* File Picker Button */}
      {buttonStyle === "compact"
        ? renderCompactButton()
        : renderDefaultButton()}

      {/* Upload Dialog */}
      <PMDialog
        open={open}
        onClose={() => setOpen(false)}
        title="Forhåndsvisning og opplasting"
      >
        {filesToUpload.length > 0 && (
          <>
            {/* Folder Selection */}
            {module === "project" && moduleParam && (
              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <FolderIcon className="h-5 w-5 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-800">Mappe:</span>
                </div>
                <ProjectFolderSelector
                  projectId={moduleParam}
                  selected={currentFolder}
                  onChange={handleFolderChange}
                />
                {currentFolder && (
                  <p className="text-xs text-blue-600 mt-1">
                    Dokumenter vil bli lastet opp til: {currentFolder.displayTitle || "Valgt mappe"}
                  </p>
                )}
              </div>
            )}

            {filesToUpload.map((file, index) => (
              <div
                key={index}
                className="flex flex-col gap-3 py-4 bg-gray-100 rounded-xl px-4 relative mb-3"
              >
                {/* Delete Button */}
                <button
                  type="button"
                  onClick={() => handleDeleteFile(index)}
                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 transition-colors duration-200 z-10"
                  title="Fjern bilde"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
                
                <img
                  src={
                    file.file.type === "application/pdf"
                      ? PdfPlaceholder
                      : file.previewUrl
                  }
                  className="h-40 object-cover rounded"
                  alt="Preview"
                />
                <PMField
                  name="title"
                  type="text"
                  label="Tittel"
                  value={file.title}
                  onChange={(e) => handleTitleChange(index, e.target.value)}
                />
              </div>
            ))}

            {/* Progress Bar */}
            {isUploading && filesToUpload.length > 0 && (
              <div className="mt-4 w-full bg-gray-300 rounded h-4">
                <div
                  className="h-full bg-blue-500 rounded"
                  style={{
                    width: `${
                      (Object.values(uploadProgress).reduce(
                        (acc, val) => acc + val,
                        0
                      ) /
                        (filesToUpload.length * 100)) *
                      100
                    }%`,
                  }}
                />
              </div>
            )}
            {/* Upload Status */}
            {isUploading && (
              <p className="text-center mt-2 text-sm text-gray-700">
                Opplasting {uploadedCount + 1} av {filesToUpload.length}
              </p>
            )}

            {/* Upload Complete Checkmark */}
            {uploadedCount === filesToUpload.length && isUploading && (
              <div className="flex justify-center items-center mt-4 text-green-500">
                <CheckCircleIcon className="h-6 w-6" />
                <span className="ml-2 text-green-600 font-semibold">
                  Opplasting fullført
                </span>
              </div>
            )}

            {/* Add More Files Button */}
            <button
              type="button"
              className="mt-4 flex items-center justify-center w-full bg-blue-500 text-white p-2 rounded"
              onClick={handleAddMoreFiles}
            >
              <CameraIcon className="h-5 w-5 mr-2" /> Legg til flere bilder
            </button>

            {/* Upload Button */}
            <div className="flex justify-between mt-4">
              <button
                className="w-20 bg-gray-600 text-white p-2 rounded"
                onClick={() => handleClose()}
              >
                Steng
              </button>

              <button
                disabled={isUploading || !uploadFunctionRef.current}
                className="w-20 p-2 rounded text-white bg-emerald-600 hover:bg-blue-500 disabled:bg-gray-400"
                onClick={() => {
                  if (!isUploading && typeof uploadFunctionRef.current === "function") {
                    setIsUploading(true);
                    setUploadedCount(0);
                    uploadFunctionRef.current().then(() => setIsUploading(false));
                  } else {
                    console.error(
                      "startUpload is not a function or is undefined:",
                      uploadFunctionRef.current
                    );
                  }
                }}
              >
                {isUploading ? "Laster opp..." : "Last opp"}
              </button>
            </div>
          </>
        )}
      </PMDialog>

      {/* Upload Handler */}
      <UploaderV4
        filesToUpload={filesToUpload}
        onUploadStart={useCallback((uploadFn) => {
          if (uploadFn && typeof uploadFn === "function") {
            uploadFunctionRef.current = uploadFn;
            setStartUpload(true); // Set to true to indicate upload function is ready
          } else {
            console.error("Invalid upload function received:", uploadFn);
          }
        }, [])}
        onUploadComplete={handleUploadComplete}
        onProgressChange={handleProgressChange}
        onFileUploadComplete={handleFileUploadComplete}
        onReset={setResetUploader}
      />
    </>
  );
};
